import { Card, CardPresentation, Label } from '@yleisradio/areena-types';
import { logError } from '../error/errorUtils';
import { getRawLabel } from './labels';
import { UiProgressBar } from '../View/viewTypes';
import { getCurrentTransmission } from '../View/utils';
import { getCSSPercentage } from './ui-helpers';
import { isoDurationToSeconds } from './duration';

export const getProgressDurationLabel = (
  labels: Label[] | undefined
): Label | undefined =>
  labels?.find(
    label => label.type === 'progress' && label.rawType == 'duration'
  );

export const getWatchedDurationLabel = (
  labels: Label[] | undefined
): Label | undefined =>
  labels?.find(
    label => label.type === 'watchedDuration' && label.rawType == 'duration'
  );

export const isCurrentBroadcastCard = (card: {
  presentation?: CardPresentation;
  labels?: Label[];
}): boolean =>
  card.presentation === 'broadcastCard' &&
  getRawLabel(card as Card, 'broadcastStatus') === 'current';

export const getWatchedDurationFromLabels = (card: { labels?: Label[] }) => {
  const { raw } = getProgressDurationLabel(card.labels) || {};

  if (raw) {
    try {
      const duration = isoDurationToSeconds(raw);

      if (duration) {
        const { raw: rawWatchedDuration } =
          getWatchedDurationLabel(card.labels) || {};

        const labelWatchedDuration =
          rawWatchedDuration && isoDurationToSeconds(rawWatchedDuration);
        if (labelWatchedDuration) {
          return labelWatchedDuration / duration;
        }

        return undefined;
      }
    } catch (error) {
      logError(error);
    }
  }
};

export const getBroadcastProgress = (
  startDate: string | undefined,
  endDate: string | undefined
): number | undefined => {
  if (startDate && endDate) {
    const startDateMs = Date.parse(startDate);
    const endDateMs = Date.parse(endDate);
    const current = new Date();
    const currentTimeMs = current.getTime();
    const progress = (currentTimeMs - startDateMs) / (endDateMs - startDateMs);
    return Math.min(1.0, progress);
  }
};

const getLiveProgress = (card: Pick<Card, 'transmissions' | 'labels'>) => {
  const currentTime = Date.now();
  const currentTransmission = getCurrentTransmission(
    currentTime,
    card.transmissions
  );

  if (currentTransmission) {
    return getBroadcastProgress(
      currentTransmission.startTime,
      currentTransmission.endTime
    );
  }

  const broadcastStartDate = getRawLabel(card, 'broadcastStartDate');
  const broadcastEndDate = getRawLabel(card, 'broadcastEndDate');

  return getBroadcastProgress(broadcastStartDate, broadcastEndDate);
};

export const getLiveCardUiProgressBar = (
  card: Pick<Card, 'transmissions' | 'labels'>,
  isSquareList: boolean
): UiProgressBar | undefined => {
  if (isSquareList) return;

  const progress = getLiveProgress(card);

  if (progress) {
    return {
      width: getCSSPercentage(progress),
      live: true,
    };
  }
};

export const getContinueCardUiProgressBarFromLabels = (
  card: Pick<Card, 'labels'>,
  isContinueList: boolean
): UiProgressBar | undefined => {
  if (!isContinueList) return;

  const progress = getWatchedDurationFromLabels(card);

  if (!progress) return;

  return {
    width: getCSSPercentage(progress),
    live: false,
  };
};

export const getCardUiProgressBarFromHistoryDuration = (
  historyWatchedSeconds: number,
  durationSeconds: number
): UiProgressBar => {
  return {
    width: getCSSPercentage(historyWatchedSeconds / durationSeconds),
    live: false,
  };
};
